/**
 * SO detail/modify mutations (S2.3) — store-first (Zustand upsert +
 * exact-key clearCache via invalidateSalesOrderCaches; the calling surface
 * re-runs its own wrapped refresh — never bare ahooks refresh()).
 *
 * Also hosts the price-hold release permission gate: release requires the
 * explicit `sales_orders.manage` grant — the banner HIDES the release
 * affordance for everyone else (hide-don't-disable).
 */

import { salesOrderApi } from '@/services/api';
import { getUserProfile } from '@/services/api/axios.config';
import { invalidateSalesOrderCaches } from '@/hooks/useSalesOrders';
import { useSoStore } from '@/stores/soStore';
import { can } from '@/lib/rbac';
import { Permission } from '@/models/user';
import type { User } from '@/models/user';
import type {
  ConfirmChecklistResult,
  SalesOrder,
  UpdateSalesOrderRequest,
} from '@/types/api/salesOrder';

/**
 * Whether the signed-in user may release a 價格審核 pending-approval hold —
 * the pure Axis-A rbac read (`can`, null-safe) over the stored profile
 * snapshot, resolver-wired in S1.3.
 *
 * MANAGER is no longer a magic role: the old `Role.ADMIN || Role.MANAGER ||
 * permissions.includes(...)` triple collapsed to the explicit
 * `sales_orders.manage` grant. Behaviour-preserving by the no-lockout seed
 * (S0.5): role-admin holds it via `[...ALL_PERMISSIONS]`, 業務 (role-sales)
 * directly, Branch Manager by inheritance — frozen by
 * `src/lib/rbac/__tests__/noLockoutParity.test.ts`.
 */
export function canReleasePriceHold(): boolean {
  return can(getUserProfile() as User | null, Permission.MANAGE_SALES_ORDERS);
}

/**
 * The confirm endpoint returns the full checklist alongside the 422 —
 * surface it so the checklist card can render the SERVER evaluation.
 */
export function extractConfirmChecklist(error: unknown): ConfirmChecklistResult | null {
  const err = error as { response?: { data?: { checklist?: ConfirmChecklistResult } } };
  return err?.response?.data?.checklist ?? null;
}

export function useSalesOrderDetailActions() {
  const { upsertSalesOrderInList } = useSoStore();

  const applyResult = (salesOrder: SalesOrder): SalesOrder => {
    upsertSalesOrderInList(salesOrder);
    invalidateSalesOrderCaches(salesOrder.id);
    return salesOrder;
  };

  const confirmSalesOrder = async (id: string): Promise<SalesOrder> => {
    const response = await salesOrderApi.confirm(id);
    return applyResult(response.data);
  };

  const releasePriceHold = async (id: string, reason: string): Promise<SalesOrder> => {
    const response = await salesOrderApi.releasePriceHold(id, reason);
    return applyResult(response.data);
  };

  /** S2.3 modify flow — draft/confirmed only; replaced lines get NEW snapshots. */
  const updateSalesOrder = async (
    id: string,
    body: UpdateSalesOrderRequest
  ): Promise<SalesOrder> => {
    const response = await salesOrderApi.update(id, body);
    return applyResult(response.data);
  };

  return { confirmSalesOrder, releasePriceHold, updateSalesOrder };
}
